import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from 'src/contexts/auth-context';
import { getEmpresaDetailsFromUser } from 'src/services/empresaService';
import useTemasDelCopiloto from 'src/components/controlObra/useTemasDelCopiloto';

/**
 * El copiloto de la app.
 *
 * Nació adentro de la pantalla de una obra y ahí se quedó: para hablarle había
 * que estar parado en una obra, y el montaje vivía en un componente de 111 KB
 * que no tiene nada que ver con conversar. Acá el copiloto se monta UNA vez, a
 * nivel aplicación, y cada pantalla le declara dónde está parada.
 *
 * Lo declarado es exactamente el `contexto` que el chat ya le manda al agente en
 * cada mensaje —`{ control_obra_id, obra_titulo }`—, no una foto paralela de la
 * pantalla: dos formas de decir lo mismo terminan discrepando.
 *
 * Desacoplar no es fingir que el copiloto mira y no toca: el copiloto CAMBIA la
 * obra. Lo que cambia es por dónde se entera la pantalla — antes eran callbacks
 * que le pasaba al panel, ahora son eventos a los que se suscribe:
 *
 * - `aplicado`: se aceptó una propuesta y lo que hay en pantalla quedó viejo.
 * - `foco`: qué nodo(s) resaltar mientras se mira una propuesta.
 *
 * Los temas NO viajan como evento y no se calculan dos veces: la cola se lee
 * una sola vez, acá, y tanto el chat como el aviso flotante de la obra leen esa
 * misma lectura. Dos lecturas de la misma cola se desincronizan al primer
 * "aceptar".
 */

const CopilotoContext = createContext(null);

export function CopilotoProvider({ children }) {
  const { user } = useAuthContext();
  const userId = user?.user_id || user?.id;

  const [contexto, setContexto] = useState(null);
  const [abierto, setAbierto] = useState(false);
  const [empresaId, setEmpresaId] = useState(null);

  const suscriptoresRef = useRef(new Map());

  const emitir = useCallback((evento, dato) => {
    suscriptoresRef.current.get(evento)?.forEach((fn) => fn(dato));
  }, []);

  const suscribir = useCallback((evento, fn) => {
    const mapa = suscriptoresRef.current;
    if (!mapa.has(evento)) mapa.set(evento, new Set());
    mapa.get(evento).add(fn);
    return () => { mapa.get(evento)?.delete(fn); };
  }, []);

  const declararContexto = useCallback((valor) => setContexto(valor), []);
  // Sólo borra si el contexto vigente sigue siendo el suyo: al cambiar de
  // pantalla la que se va se desmonta después de que la nueva declaró.
  const olvidarContexto = useCallback(
    (valor) => setContexto((actual) => (actual === valor ? null : actual)),
    [],
  );

  // La empresa sólo hace falta para leer la cola de propuestas, así que se
  // resuelve recién cuando hay una obra declarada: el resto de la app no paga
  // una llamada por tener el copiloto montado.
  const obraDeclarada = contexto?.control_obra_id || null;
  useEffect(() => {
    if (!obraDeclarada || !user) return;
    let vigente = true;
    getEmpresaDetailsFromUser(user)
      .then((e) => { if (vigente) setEmpresaId(e?.id || null); })
      .catch(() => {});
    return () => { vigente = false; };
  }, [obraDeclarada, user]);

  // Sin empresa todavía no se puede leer nada, y la cola se pide una sola vez
  // por obra: se la deja en null hasta tener las dos cosas.
  const obra = useMemo(
    () => (obraDeclarada && empresaId ? { _id: obraDeclarada, titulo: contexto?.obra_titulo || null } : null),
    [obraDeclarada, empresaId, contexto?.obra_titulo],
  );

  const onAplicado = useCallback(() => emitir('aplicado'), [emitir]);
  const q = useTemasDelCopiloto({ obra, empresaId, userId, onAplicado });

  // Le avisa a la pantalla qué filas resaltar: el efecto se ve en la tabla ANTES
  // de aceptar, que es la diferencia entre confiar y confirmar a ciegas. En un
  // tema son todas, porque de todas ésas está hablando.
  useEffect(() => {
    if (!abierto || !q.tema) { emitir('foco', null); return; }
    emitir('foco', q.enDetalle
      ? (q.actual?.destino?.nodo_uid || null)
      : q.tema.items.map((p) => p.destino?.nodo_uid).filter(Boolean));
  }, [abierto, q.tema, q.enDetalle, q.actual, emitir]);

  const abrir = useCallback(() => setAbierto(true), []);
  const cerrar = useCallback(() => setAbierto(false), []);

  const value = useMemo(
    () => ({ contexto, abierto, abrir, cerrar, q, suscribir, declararContexto, olvidarContexto, avisarAplicado: onAplicado }),
    [contexto, abierto, abrir, cerrar, q, suscribir, declararContexto, olvidarContexto, onAplicado],
  );

  return <CopilotoContext.Provider value={value}>{children}</CopilotoContext.Provider>;
}

export function useCopiloto() {
  const ctx = useContext(CopilotoContext);
  if (!ctx) throw new Error('useCopiloto tiene que usarse adentro de CopilotoProvider');
  return ctx;
}

/**
 * Lo que esta pantalla le dice al copiloto sobre dónde está parada. Se limpia al
 * desmontar: un copiloto que sigue creyendo que hay una obra abierta después de
 * salir de ella contesta sobre la obra equivocada, que es el peor error posible
 * hablando de plata.
 */
export function useContextoDelCopiloto(contexto) {
  const { declararContexto, olvidarContexto } = useCopiloto();
  // Serializado a propósito: la pantalla declara un objeto y si dependiéramos de
  // su identidad, un literal en el render volvería a declarar en cada vuelta.
  const clave = contexto ? JSON.stringify(contexto) : null;

  useEffect(() => {
    const valor = clave ? JSON.parse(clave) : null;
    declararContexto(valor);
    return () => olvidarContexto(valor);
  }, [clave, declararContexto, olvidarContexto]);
}

/** Suscribe la pantalla a lo que el copiloto hace: `aplicado`, `foco`. */
export function useCopilotoEvento(evento, handler) {
  const { suscribir } = useCopiloto();
  // El handler viaja en un ref para no re-suscribir en cada render: las
  // pantallas pasan funciones nuevas cada vuelta y volver a suscribir perdería
  // eventos disparados en el medio.
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => suscribir(evento, (dato) => ref.current?.(dato)), [evento, suscribir]);
}
